import React from "react";
import {
  Card,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Icon,
  Chip,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";

const RefundReportsTable = ({ report }) => {
  const {
    placeData,
    refundData = [],
    totalRefund,
    startDate,
    transactionType = "daily",
  } = report;

  const amountToShow = (val) => (Number(val || 0) / 100).toFixed(2);
  const totalRefundAmount = refundData.reduce(
    (acc, curr) => acc + (curr.amount || 0),
    0,
  );

  // Date & Label Logic
  const reportLabel =
    transactionType.charAt(0).toUpperCase() +
    transactionType.slice(1) +
    " Refund Report";
  let dateDisplay = moment(startDate).format("MM/DD/YYYY");

  if (transactionType === "weekly") {
    const end = moment(startDate).add(6, "days");
    dateDisplay = `${moment(startDate).format("MM/DD/YYYY")} - ${end.format("MM/DD/YYYY")}`;
  } else if (transactionType === "monthly") {
    const end = moment(startDate).endOf("month");
    dateDisplay = `${moment(startDate).format("MM/DD/YYYY")} - ${end.format("MM/DD/YYYY")}`;
  }

  // --- PDF GENERATION ---
  const downloadPdf = () => {
    const doc = new jsPDF({ orientation: "landscape", format: "a4" });

    // 1. Header
    doc.setFontSize(18);
    doc.text(`${placeData?.parkingCode} - ${reportLabel}`, 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Location: ${placeData?.locationAddress}`, 14, 26);
    doc.text(`Period: ${dateDisplay}`, 14, 31);

    // 2. Synopsis Section (Colored)
    doc.setDrawColor(220);
    doc.line(14, 36, 280, 36);
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.text("Synopsis:", 14, 44);

    // Total Count
    doc.text(`Count: ${totalRefund}`, 35, 44);

    // Total Amount (Red - to match UI)
    doc.setTextColor(211, 47, 47); // #d32f2f
    doc.setFont(undefined, "bold");
    doc.text(`Total Amount: $${amountToShow(totalRefundAmount)}`, 70, 44);

    // 3. Table Data
    const header = [
      "Date",
      "Transaction ID",
      "Refund ID",
      "Customer Name",
      "Contact",
      "Amount",
    ];
    const body = refundData.map((r) => [
      moment(r.transactionDate).format("MM/DD/YYYY"),
      r.transactionId,
      r.refundTransactionId,
      r.lastName,
      r.email || r.mobile,
      `$${amountToShow(r.amount)}`,
    ]);

    body.push([
      "",
      "",
      "",
      "",
      "TOTAL REFUNDED:",
      `$${amountToShow(totalRefundAmount)}`,
    ]);

    autoTable(doc, {
      head: [header],
      body,
      startY: 50,
      theme: "grid",
      styles: {
        fontSize: 9,
        cellPadding: 2,
        valign: "middle",
        lineColor: [224, 224, 224],
      },
      headStyles: {
        fillColor: [240, 242, 245],
        textColor: [52, 71, 103],
        fontStyle: "bold",
        lineWidth: 0.1,
      },
      didParseCell: (data) => {
        // Right align Amount
        if (data.column.index === 5) {
          data.cell.styles.halign = "right";
          // Red text for amounts
          if (data.section === "body" && data.row.index < body.length - 1) {
            data.cell.styles.textColor = [211, 47, 47];
          }
        }

        // Style the "Total" row at the bottom
        if (data.row.index === body.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [255, 235, 238]; // Light Red BG
          if (data.column.index === 5)
            data.cell.styles.textColor = [211, 47, 47];
        }
      },
    });

    doc.save(`${reportLabel}-${placeData?.parkingCode}.pdf`);
  };

  const SummaryCard = ({ icon, title, value, color }) => (
    <Card sx={{ height: "100%", boxShadow: 2, overflow: "visible" }}>
      <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
        <MDBox
          variant="gradient"
          bgColor={color}
          color="white"
          coloredShadow={color}
          borderRadius="lg"
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="3rem"
          height="3rem"
          mt={-2}
        >
          <Icon fontSize="small" color="inherit">
            {icon}
          </Icon>
        </MDBox>
        <MDBox textAlign="right" lineHeight={1.25}>
          <MDTypography
            variant="button"
            fontWeight="light"
            color="text"
            fontSize="0.75rem"
          >
            {title}
          </MDTypography>
          <MDTypography variant="h5" fontWeight="bold">
            {value}
          </MDTypography>
        </MDBox>
      </MDBox>
      <Divider sx={{ my: 1 }} />
      <MDBox pb={1} px={2}>
        <MDTypography component="p" variant="caption" color="text">
          {dateDisplay}
        </MDTypography>
      </MDBox>
    </Card>
  );

  return (
    <MDBox pt={2} pb={3}>
      {/* 1. Header Card */}
      <Card sx={{ mb: 3, p: 2, boxShadow: 2 }}>
        <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
          <Grid item>
            <MDTypography variant="h5" fontWeight="medium">
              {placeData?.parkingCode} <span style={{ opacity: 0.5 }}>|</span>{" "}
              {reportLabel}
            </MDTypography>
            <MDTypography variant="button" color="text" display="block">
              {placeData?.locationAddress}
            </MDTypography>
            <MDBox display="flex" alignItems="center" gap={1} mt={0.5}>
              <Chip
                label={dateDisplay}
                size="small"
                variant="outlined"
                color="error"
              />
              {transactionType === "weekly" && (
                <Chip
                  label={moment(startDate).format("dddd")}
                  size="small"
                  color="secondary"
                />
              )}
            </MDBox>
          </Grid>
          <Grid item>
            <MDButton
              variant="gradient"
              color="error"
              size="small"
              onClick={downloadPdf}
            >
              Download PDF
            </MDButton>
          </Grid>
        </Grid>
      </Card>

      {/* 2. Stats */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <SummaryCard
            icon="replay"
            title="Total Refunds"
            value={totalRefund}
            color="error"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <SummaryCard
            icon="monetization_on"
            title="Refund Amount"
            value={`$${amountToShow(totalRefundAmount)}`}
            color="dark"
          />
        </Grid>
      </Grid>

      {/* 3. Table with BORDERS */}
      <Card sx={{ boxShadow: 3 }}>
        <MDBox p={2}>
          <MDTypography variant="h6" fontWeight="medium">
            Refund Breakdown
          </MDTypography>
        </MDBox>

        <MDBox>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small" aria-label="refund report table">
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  <TableCell
                    sx={{
                      minWidth: 100,
                      fontWeight: "bold",
                      py: 1.5,
                      border: "1px solid #f0f2f5",
                    }}
                  >
                    Date
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: 130,
                      fontWeight: "bold",
                      py: 1.5,
                      border: "1px solid #f0f2f5",
                    }}
                  >
                    Transaction ID
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: 130,
                      fontWeight: "bold",
                      py: 1.5,
                      border: "1px solid #f0f2f5",
                    }}
                  >
                    Refund ID
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: 150,
                      fontWeight: "bold",
                      py: 1.5,
                      border: "1px solid #f0f2f5",
                    }}
                  >
                    Customer
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: 150,
                      fontWeight: "bold",
                      py: 1.5,
                      border: "1px solid #f0f2f5",
                    }}
                  >
                    Contact
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      minWidth: 100,
                      fontWeight: "bold",
                      py: 1.5,
                      border: "1px solid #f0f2f5",
                    }}
                  >
                    Amount
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {refundData.length > 0 ? (
                  refundData.map((r, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ py: 1, border: "1px solid #f0f2f5" }}>
                        {moment(r.transactionDate).format("MM/DD/YYYY")}
                      </TableCell>
                      <TableCell sx={{ py: 1, border: "1px solid #f0f2f5" }}>
                        {r.transactionId}
                      </TableCell>
                      <TableCell sx={{ py: 1, border: "1px solid #f0f2f5" }}>
                        {r.refundTransactionId}
                      </TableCell>
                      <TableCell sx={{ py: 1, border: "1px solid #f0f2f5" }}>
                        {r.lastName}
                      </TableCell>
                      <TableCell sx={{ py: 1, border: "1px solid #f0f2f5" }}>
                        {r.email || r.mobile}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: "error.main",
                          fontWeight: "bold",
                          py: 1,
                          border: "1px solid #f0f2f5",
                        }}
                      >
                        -${amountToShow(r.amount)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      align="center"
                      sx={{ border: "1px solid #f0f2f5" }}
                    >
                      <MDTypography variant="body2" py={3}>
                        No refunds found.
                      </MDTypography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </MDBox>
      </Card>
    </MDBox>
  );
};

export default RefundReportsTable;
